import {
    Component,
    Input,
    Output,
    OnInit,
    EventEmitter,
    ContentChild,
    TemplateRef,
    ElementRef
} from '@angular/core';
import { DisplayableInTree } from '../../core-data-model/DisplayableInTree';
import { FoldersDataService } from '../../core-services/Folders.dataservice';
import { Util } from '../../core-utilities/Util';

export type FolderTreeEntryComponentOptions = {
    depthOpenUntil: number;
    startOpen: boolean;
    treeSelection: {
        item?: DisplayableInTree;
    };
    allowRootFolderAccess: boolean;
};

@Component({
    selector: 'folder-tree-entry',
    template: `
        <ng-template
            #nodeTemplateRef
            let-item="item"
            let-options="options"
            let-state="state"
        >
            @if (item) {
                <div class="k-widget k-treeview k-reset" tabindex="0">
                    <div
                        class="folderTree-row k-content"
                        [ngClass]="{
                            largeBoldText:
                                isRootFolder(item) &&
                                !options.allowRootFolderAccess
                        }"
                    >
                        <span
                            class="pi-folder-icon"
                            [ngClass]="{
                                'pi-folder-icon--open': state.open,
                                'pi-folder-icon--hasSubfolders':
                                    item.children.length,
                                'TESTHOOK-pi-folder-icon--closed': !state.open
                            }"
                            (click)="state.open = !state.open"
                        >
                            <i class="fa"></i>
                        </span>
                        <span
                            class="k-in"
                            [ngClass]="{
                                'k-state-selected':
                                    item.id === selectedItem?.id,
                                'disable-interaction':
                                    isRootFolder(item) &&
                                    !options.allowRootFolderAccess
                            }"
                            (click)="selectItem(item)"
                        >
                            <ng-template
                                [ngTemplateOutlet]="contentTemplate"
                                [ngTemplateOutletContext]="{
                                    item: item
                                }"
                            >
                            </ng-template>
                        </span>
                    </div>
                    @if (state.open && item.children.length) {
                        <ul class="k-group">
                            @for (child of item.children; track child) {
                                <li class="k-item">
                                    <!-- Invoke the recursive template. -->
                                    <ng-template
                                        [ngTemplateOutlet]="nodeTemplateRef"
                                        [ngTemplateOutletContext]="{
                                            item: child,
                                            options: incrementOptions(options),
                                            state: {
                                                open: incrementOptions(options)
                                                    .startOpen
                                            }
                                        }"
                                    >
                                        <!--
                      Because we are using nested ngTemplates (rather than the template
                      input of the ngFor directive), we have more control over how the
                      data is made available to the recursive template. In this case,
                      we're passing "child" through as "node".
                      -->
                                    </ng-template>
                                </li>
                            }
                        </ul>
                    }
                </div>
            }
        </ng-template>
        <ng-template
            [ngTemplateOutlet]="nodeTemplateRef"
            [ngTemplateOutletContext]="{
                item: item,
                options: options,
                state: { open: options.startOpen }
            }"
        >
        </ng-template>
    `,
    standalone: false
})
export class FolderTreeEntryComponent implements OnInit {
    @ContentChild('nodeDisplay', { static: false })
    contentTemplate: TemplateRef<ElementRef>;

    @Input()
    item: DisplayableInTree;
    @Input()
    options: FolderTreeEntryComponentOptions =
        {} as FolderTreeEntryComponentOptions;
    @Output()
    readonly folderSelected = new EventEmitter<DisplayableInTree>();

    selectedItem: DisplayableInTree;

    constructor(
        private folderService: FoldersDataService,
        private util: Util
    ) {}

    ngOnInit() {
        // Defaults for options object
        this.options.treeSelection = this.options.treeSelection || {
            item: undefined
        };
        // Set default value of root folder interaction to true
        this.options.allowRootFolderAccess = this.util.hasValue(
            this.options.allowRootFolderAccess
        )
            ? this.options.allowRootFolderAccess
            : true;
    }

    selectItem(item: DisplayableInTree): void {
        if (!this.isRootFolder(item) || this.options.allowRootFolderAccess) {
            this.selectedItem = item;
            this.folderSelected.emit(item);
        }
    }

    isRootFolder(item: DisplayableInTree): boolean {
        return item.id === this.folderService.rootFolder.id;
    }

    incrementOptions(
        options: FolderTreeEntryComponentOptions
    ): FolderTreeEntryComponentOptions {
        return Object.assign({}, options, {
            depthOpenUntil: this.util.hasValue(options.depthOpenUntil)
                ? this.options.depthOpenUntil - 1
                : null,
            startOpen: this.util.hasValue(options.depthOpenUntil)
                ? this.options.depthOpenUntil > 0
                : true
        });
    }
}
